import { Component, computed, HostBinding, input, linkedSignal, model, output } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { ThemeColor } from '../helpers';

@Component({
  selector: 'app-pan-datetime-picker',
  templateUrl: './pan-datetime-picker.component.html',
  standalone: false,
})
export class PanDatetimepickerComponent {
  public color = input<ThemeColor>('default');
  public disabled = input<boolean>(false);
  public min = input<Date>();
  public max = input<Date>();
  public name = input<string>();
  public placeholder = input<string>();
  public id = input<string>();
  public dateStartView = input<'month' | 'year' | 'multi-year'>('month');
  public dateLabel = input<string>('Date (MM/DD/YYYY)');
  public timeLabel = input<string>('Time (HH:MM AM/PM)');

  public dateMonthSelected = output<{ date: Date; datePicker: MatDatepicker<Date> }>();
  public dateYearSelected = output<{ date: Date; datePicker: MatDatepicker<Date> }>();
  public dateChange = output<string | Date>();
  public invalidDate = output<void>();

  public dateTime = model<string | Date | null>();
  public dateValue = linkedSignal<string | Date | null>(() => this.dateTime() ?? null);
  public timeStr = linkedSignal<string | null>(() => {
    const inputDate = this.dateTime();
    if (!inputDate || !(inputDate instanceof Date)) return null;
    return this.getTimePart(inputDate);
  });

  @HostBinding('class')
  public get hostClasses() {
    return this.datePickerClasses().join(' ');
  }

  public datePickerClasses = computed(() => {
    return ['datepicker', `datepicker-color-${this.color()}`, this.disabled() ? 'datepicker-disabled' : ''].filter(Boolean);
  });

  public timePickerClasses = computed(() => {
    return ['timepicker', `timepicker-color-${this.color()}`, this.disabled() ? 'timepicker-disabled' : ''].filter(Boolean);
  });

  dateMonthSelectedHandler(event: { date: Date; datePicker: MatDatepicker<Date> }) {
    this.dateMonthSelected.emit({ date: event.date, datePicker: event.datePicker });
  }

  dateYearSelectedHandler(event: { date: Date; datePicker: MatDatepicker<Date> }) {
    this.dateYearSelected.emit({ date: event.date, datePicker: event.datePicker });
  }

  public onDateChange($event: string | Date): void {
    this.dateChange.emit($event);
    this.dateValue.set($event);
    this.combineDateAndTime();
  }

  public onTimeChange($event: string): void {
    if (!this.isValidTime($event)) return;

    this.timeStr.set($event);

    if (!this.dateValue()) {
      return;
    }

    this.combineDateAndTime();
  }

  private combineDateAndTime(): void {
    const currentDate = this.dateValue();
    const currentTime = this.timeStr();

    if (!currentDate || !currentTime) {
      return;
    }

    const [hours, minutes] = currentTime.split(':').map(Number);
    const combinedDateTime = new Date(currentDate);
    combinedDateTime.setHours(hours, minutes, 0, 0);

    this.dateTime.set(combinedDateTime);
  }

  private isValidTime(value: string): boolean {
    const match = /^(\d{2}):(\d{2})$/.exec(value);
    if (!match) {
      return false;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);

    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
  }

  private getTimePart(value: Date): string {
    const hours = value.getHours().toString().padStart(2, '0');
    const minutes = value.getMinutes().toString().padStart(2, '0');

    return `${hours}:${minutes}`;
  }
}
